/**
 * Sitios de automotoras en goauto.lat (fuera de Chile).
 *
 * En la base el sitio de cada automotora sigue guardado como `{slug}.goauto.cl`
 * (`clients.domain`). Para las automotoras de fuera de Chile la dirección
 * principal pasa a ser `{slug}.goauto.lat`, sin tocar la base:
 *
 *   - `{slug}.goauto.lat` busca la automotora `{slug}.goauto.cl`.
 *   - Fuera de Chile (country ≠ 'CL'): `{slug}.goauto.cl` redirige a `.lat`.
 *   - Chile (o país vacío): `{slug}.goauto.lat` redirige a `.cl`; el `.cl`
 *     queda EXACTAMENTE como siempre.
 *   - Dominio propio (`custom_domain`) y cualquier otro host: sin cambios.
 *
 * Funciones puras, sin red: las usan el middleware, sitemap, llms.txt y el
 * ClientProvider. Harness: `npm run test:host-lat`.
 */

const ZONA_CL = 'goauto.cl';
const ZONA_LAT = 'goauto.lat';

/** Subdominios de la plataforma que no son sitios de automotoras. */
const RESERVADOS = new Set(['www', 'portal', 'dev', 'api', 'app', 'test', 'mail']);

/** Una sola etiqueta DNS: letras, números y guiones (sin puntos). */
const ETIQUETA = '[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?';
const RE_LAT = new RegExp(`^(${ETIQUETA})\\.goauto\\.lat$`);
const RE_CL = new RegExp(`^(${ETIQUETA})\\.goauto\\.cl$`);

type TenantHost = {
  domain?: string | null;
  custom_domain?: string | null;
  country?: string | null;
};

function slugDe(host: string, re: RegExp): string | null {
  const m = host.toLowerCase().match(re);
  if (!m || RESERVADOS.has(m[1])) return null;
  return m[1];
}

/**
 * Host con el que se busca la automotora en `clients.domain`/`custom_domain`.
 * `{slug}.goauto.lat` → `{slug}.goauto.cl`; cualquier otro host sale tal cual
 * (mismo valor que antes, byte a byte).
 */
export function hostDeBusqueda<T extends string | null | undefined>(host: T): T | string {
  if (!host) return host;
  const slug = slugDe(host, RE_LAT);
  return slug ? `${slug}.${ZONA_CL}` : host;
}

/** País fuera de Chile: cualquier código distinto de 'CL'. Vacío = Chile. */
function esFueraDeChile(country: string | null | undefined): boolean {
  const c = (country ?? '').trim().toUpperCase();
  return c !== '' && c !== 'CL';
}

/**
 * Si el sitio se pidió por la dirección que NO es la principal, devuelve el
 * host principal al que hay que redirigir; si no, null.
 */
export function hostPrincipal(host: string | null | undefined, client: TenantHost | null | undefined): string | null {
  if (!host || !client) return null;
  const pedido = host.toLowerCase();
  const fuera = esFueraDeChile(client.country);

  // Pedido por .lat: el tenant se encontró como {slug}.goauto.cl. Chile vuelve
  // a ese .cl (el mismo host con que se lo encontró, sea domain o
  // custom_domain); fuera de Chile se queda.
  const slugLat = slugDe(pedido, RE_LAT);
  if (slugLat) return fuera ? null : `${slugLat}.${ZONA_CL}`;

  // Pedido por .cl: solo se mueve si es el `domain` del tenant ({slug}.goauto.cl)
  // y el tenant es de fuera de Chile. Chile y dominios propios: sin cambios.
  const slugCl = slugDe(pedido, RE_CL);
  if (slugCl && fuera && pedido === (client.domain ?? '').toLowerCase()) return `${slugCl}.${ZONA_LAT}`;

  return null;
}
